// Typed error classes for the widget's network layer.
//
// These are thrown by SSEClient / ApiClient / CapabilitiesClient and caught
// by the root component (T12), which decides whether to fall back to anonymous
// mode, show an oob bubble, or fire an `emma-chat-error` event.
//
// `instanceof` checks work across the Lit/Vite bundle because we aren't
// shipping multiple copies of these classes — they're imported once.

/**
 * Generic network-layer error: fetch threw, response was non-2xx, or
 * an unexpected condition arose during HTTP communication.
 */
export class NetworkError extends Error {
  /** HTTP status code if the response was received; 0 if fetch threw. */
  readonly status: number;
  /** Raw response body text if available, for diagnostic logging. */
  readonly body: string | undefined;

  constructor(message: string, status: number = 0, body?: string) {
    super(message);
    this.name = 'NetworkError';
    this.status = status;
    this.body = body;
  }
}

/**
 * 429 Too Many Requests. Distinct subclass so callers can show a different
 * user-facing message than for other 4xx errors.
 */
export class RateLimitError extends NetworkError {
  /** Value of the Retry-After header in seconds, if present. */
  readonly retryAfterSeconds: number | undefined;

  constructor(message: string, body?: string, retryAfterSeconds?: number) {
    super(message, 429, body);
    this.name = 'RateLimitError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * SSE-frame or JSON parse failure. Non-fatal at the SSEClient level —
 * the parser logs and continues. Surfaced for diagnostic purposes when
 * the malformed frame discards an event.
 */
export class ParseError extends Error {
  /** Raw fragment that failed to parse. */
  readonly fragment: string | undefined;

  constructor(message: string, fragment?: string) {
    super(message);
    this.name = 'ParseError';
    this.fragment = fragment;
  }
}

/**
 * Authorization rejected. Distinct subclass so the root component can
 * fall back to anonymous mode and emit `emma-chat-auth-error` event.
 * Mapped from HTTP 401 / 403 per T13's auth contract error matrix.
 */
export class AuthError extends NetworkError {
  constructor(message: string, status: number = 401, body?: string) {
    super(message, status, body);
    this.name = 'AuthError';
  }
}

/**
 * Resource not found (HTTP 404). Used by GET /api/hero/config when the
 * carry-deploy configId can't be located in the given dataCenter — e.g.
 * emma deprecated the SKU between the user's landing click and signup
 * completion. The widget renders an oob bubble with a refine fallback.
 */
export class NotFoundError extends NetworkError {
  constructor(message: string, body?: string) {
    super(message, 404, body);
    this.name = 'NotFoundError';
  }
}
